import * as React from 'react';
import {withStyles, WithStyles} from '@material-ui/core';
import TranslateNew from '@translate/TranslateNew';
import { ChatStyle } from './Chat.style';
import { ICall } from '@app-types/websocket/calls.types';
import PhoneIcon from '@material-ui/icons/CallRounded';
import PhoneEndIcon from '@material-ui/icons/CallEndRounded';
import Button from '@components/common/button/Button';
import { IFamilyMember } from '@app-types/websocket/family.types';
import { Link } from 'react-router-dom';
import { styleColors } from 'src/main.style';

const tn: TranslateNew = TranslateNew.getInstance();

/**
 * Calls started before this were recorded by a backend that marked people as having turned
 * a call down when in truth it had simply ended around them - the caller included. A DENIED
 * row from back then therefore proves nothing about who ended the call, so a call that never
 * connected reports only how long it rang, which is true whatever really happened. Calls
 * that did connect are left alone: an ANSWER row has always meant somebody picked up.
 *
 * A fixed point in time on purpose - "before today" would quietly reclassify calls as the
 * days pass. Move it if the fix goes live on a later date.
 */
const PRECISE_CALL_HISTORY_FROM = new Date(2026, 7, 21).getTime();

interface IProps extends WithStyles<typeof ChatStyle> {
  callId:number;
  call?:ICall;
  family:IFamilyMember[];
  callHistory:(id:number) => void;
}

class ChatCallMessage extends React.Component<IProps> {

  constructor(props: IProps) {
    super(props);
  }

  public render() {

    const {classes, callId, call} = this.props;
    
    let icon = <PhoneIcon style={{color:styleColors.greyMedium3, fontSize:32, margin:'auto'}} />

    let info:JSX.Element = <div style={{ display: 'flex', flex: 1, justifyContent: 'center'}}>
      <Button variant='contained' classes={{outlined:classes.buttonContainedLarge}}
        style={{backgroundColor: styleColors.greyMedium1, color: styleColors.greyDark2, boxShadow: 'none'}}
        onClick={() => this.props.callHistory(callId)} >
      {tn.lang.details}</Button></div>

    if (call !== undefined ) {

      let title:string = tn.lang.unknown;
      const statusRows:string[] = [];
      let thereWasACall = false;
      // Only a terminal response from the caller means the caller gave up. Anybody else is
      // just recorded as having left, so a call the other side turned down is not reported
      // as the caller hanging up.
      let callerGaveUp = false;
      // When nobody answered, how long it rang: up to the first person who was done with it.
      let ringEndedAt:number|undefined;

      const participants = Array.isArray(call.participants) ? call.participants : [];

      for (const part of participants) {
        if (part.participant !== call.initiator && part.responseStatus === 'ANSWER') {
          thereWasACall = true;
        }
        if (part.participant === call.initiator
          && (part.responseStatus === 'DENIED' || part.responseStatus === 'HANG_UP')) {
          callerGaveUp = true;
        }

        const endedAt = part.responseTime !== undefined && part.responseTime > 0
          ? part.responseTime
          : (part.cleanExitTime !== undefined && part.cleanExitTime > 0 ? part.cleanExitTime : undefined);
        if (endedAt !== undefined && (ringEndedAt === undefined || endedAt < ringEndedAt)) {
          ringEndedAt = endedAt;
        }
      }

      const ringSeconds:string|undefined = ringEndedAt === undefined
        ? undefined
        : Math.max(0, (ringEndedAt - call.start) / 1000 - 3).toFixed(0);

      const preciseHistory:boolean = call.start >= PRECISE_CALL_HISTORY_FROM;
      // Whether this call's records can say who ended it, or only that it rang.
      const describeOutcome:boolean = preciseHistory || thereWasACall;

      // The call lasted until the last person who was actually in it left. Exit times of
      // people who never answered say nothing about how long the call ran.
      let lastExit:number|undefined;
      for (const part of participants) {
        if (part.responseStatus === 'ANSWER' && part.cleanExitTime !== undefined && part.cleanExitTime > 0
          && (lastExit === undefined || part.cleanExitTime > lastExit)) {
          lastExit = part.cleanExitTime;
        }
      }
      const duration:number|undefined = lastExit === undefined
        ? undefined
        : Math.max(1, Math.ceil((lastExit - call.start)/(1000*60)));

      for (const part of participants) {
        if (part.participant === call.initiator) {
          const initiator = tn.familyMemberName(part.participant, this.props.family);
          title = tn.formatTime(new Date(call.start))+" "+ tn.lang.called(initiator);
          if (!thereWasACall && ringSeconds !== undefined) {
            title += (describeOutcome && callerGaveUp)
              ? tn.lang.calledFor(ringSeconds)
              : tn.lang.callRangFor(ringSeconds);
          }

          if (part.responseStatus === 'TECH_ERROR' && part.responseTime) {
            const seconds = (part.responseTime - call.start)/ 1000;
            statusRows.push(tn.lang.callTechErrorSeconds(tn.familyMemberName(part.participant, this.props.family), seconds.toFixed(0)));
          }
        } else {
          if (part.responseStatus === 'ANSWER') {
            statusRows.push(tn.lang.callAnswered(tn.familyMemberName(part.participant, this.props.family)));
          } else if (!describeOutcome) {
            // Nothing trustworthy to say about this person, so say nothing.
          } else if (part.responseStatus === 'DENIED') {
            // Turned the call down themselves, which is not the same as never picking up.
            statusRows.push(preciseHistory
              ? tn.lang.callDeclined(tn.familyMemberName(part.participant, this.props.family))
              : tn.lang.callNoAnswer(tn.familyMemberName(part.participant, this.props.family)));
          } else if (part.responseStatus === 'TECH_ERROR') {
            statusRows.push(tn.lang.callTechError(tn.familyMemberName(part.participant, this.props.family)));
          } else if (part.responseStatus === 'AWAITING') {
            if (Date.now() - call.start < 30*1000 ) {
              // statusRows.push(tn.familyMemberName(part.participant, this.props.family) + );
            } else {
              statusRows.push(tn.lang.callNoAnswer(tn.familyMemberName(part.participant, this.props.family)));
            }
            
          } else {
            // Anything else (hung up before answering, no response, unknown) reads as
            // "did not answer" rather than leaking a protocol value into the chat.
            statusRows.push(tn.lang.callNoAnswer(tn.familyMemberName(part.participant, this.props.family)));
          }
        }

      }

      let downloadLink:JSX.Element|undefined

      if (duration !== undefined && thereWasACall) {

        if (call.recording === undefined) {
          if (call.start + duration*60*1000 + 2*60*1000 > Date.now()) {
            downloadLink = <div key={"link"} style={{fontStyle:'italic'}}>{tn.lang.callRecording}</div>
          } else {
            downloadLink = <div key={"link"}>{tn.lang.callDuration(duration)}</div>
          }
        } else {
          downloadLink = <a key={"link"} href={call.recording} target="_blank" rel="noopener noreferrer">{tn.lang.callDuration(duration)}</a>
        }
      }

      if (thereWasACall) {
        icon = <PhoneIcon style={{color:styleColors.callGreen, fontSize:32, margin:'auto'}} />
        info =  <div className={classes.callInfo}>
          <div className={classes.callTitle}>{title}</div>
          {statusRows.map((row:string, index:number) => (<div key={index}>{row}</div>))}
          {downloadLink}
          <div style={{paddingTop: '8px', fontStyle: 'italic', fontSize: '12px'}}>{tn.lang.callNewFunctionality}</div>
          <div style={{paddingTop: '8px', fontStyle: 'italic', fontSize: '12px'}}>{tn.lang.callTurnOff}
            <Link to={`/app/settings/:userId`} >{tn.lang.settings}</Link>
          </div>
        </div>
      } else {
        icon = <PhoneEndIcon style={{color:styleColors.callRed, fontSize:32, margin:'auto'}} />
        info =  <div className={classes.callInfo}>
          <div className={classes.callTitle}>{title}</div>
          {statusRows.map((row:string, index:number) => (<div key={index}>{row}</div>))}
          <div style={{paddingTop: '8px', fontStyle: 'italic', fontSize: '12px'}}>{tn.lang.callNewFunctionality}</div>
          <div style={{paddingTop: '8px', fontStyle: 'italic', fontSize: '12px'}}>{tn.lang.callTurnOff}
            <Link to={`/app/settings/:userId`} >{tn.lang.settings}</Link>
          </div>
        </div>
      }
    }

    return (
      <div className={classes.chatMessage} style={{justifyContent: 'center', marginBottom:'12px'}}>
        <div className={classes.chatMessageMsg} style={{flexDirection:'row', gap: '12px', alignItems: 'center', minWidth:'300px'}}>
          <div>{icon}</div>
          {info}
        </div>
      </div>

    );  
  }

}


export default (withStyles(ChatStyle)(ChatCallMessage));



// WEBPACK FOOTER //
// ./src/components/chat/ChatCallMessage.tsx